import { motion } from 'framer-motion';
import { IoIosAlert } from "react-icons/io";
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PatternFormat } from 'react-number-format';
import dayjs from 'dayjs';
import API from '../Common/API';

const FieldInput = ({
    type = "text",
    label,
    fieldProps = {},
    injectClass = "",
    disabled = false,
    fieldHelper,
    fieldMeta = {},
    onChange,
    autoComplete = "true",
    placeholder,
    edit,
    dataLoaded = false,
    ml,
    length,
    labelInjectClass,
    minDate,
    maxDate,
    instruction = null,
    onEnter,
    outerInjectClass,
    required = false,
    isRequired = false,
    dateInjectClass,
    translate
}) => {
    const input = useRef(null);
    const [showError, setShowError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [translatedText, setTranslatedText] = useState('');
    const [actualValue, setActualValue] = useState('');

    const hasError = Boolean(edit && fieldMeta?.error && fieldMeta?.touched);
    const inputType = useMemo(() => (type === "password" ? (showPassword ? "text" : "password") : type), [type, showPassword]);

    const setFieldValue = (value) => {
        if (fieldHelper && typeof fieldHelper.setValue === 'function') fieldHelper.setValue(value);
    };

    const setFieldTouched = (value = true) => {
        if (fieldHelper && typeof fieldHelper.setTouched === 'function') fieldHelper.setTouched(value);
    };

    const runChange = (value) => {
        setFieldValue(value);                          // always keep Formik in sync
        if (typeof onChange === 'function') {
            onChange(value);                           // also call the custom handler
        }
    };

    const handleEnterKey = (event) => {
        if (event.key === 'Enter' && typeof onEnter === 'function') onEnter(event.target.value);
    };

    const handleInputChange = (event) => {
        const value = event?.target?.value ?? event;

        if (type === 'file' && typeof onChange === 'function') {
            setFieldValue(event.target.value);
            setFieldTouched(true);
            setTimeout(() => onChange(event.target.files[0]), 100);
            return;
        }

        if (translate) {
            setFieldValue(value);
            translateFormHandling(value);
            return;
        }

        runChange(value);
    };

    useEffect(() => {
        if (type === 'file' && fieldMeta && fieldMeta.touched === false && input.current) {
            input.current.value = null;
            setFieldValue('');
        }
    }, [fieldMeta?.touched, fieldProps?.value]); // eslint-disable-line

    const handleDateChange = (newValue) => {
        const formattedDate = dayjs(newValue).format('YYYY-MM-DD');
        setFieldValue(formattedDate);
        setFieldTouched(true);
        if (typeof onChange === 'function') onChange(formattedDate);
    };

    const translateFormHandling = async (text) => {
        setTranslatedText('');
        setActualValue(text);

        if (!text?.toString()?.length) return;

        try {
            const res = await API.post('/common/translate', { text });
            if (res.status === 200) setTranslatedText(res.data.data.translatedText);
        } catch (error) {
            console.error("Translation API Error:", error);
        }
    };

    const wrapperClasses = [
        "relative mt-3 mb-2",
        outerInjectClass || "",
    ].join(" ");

    const fieldContainerClasses = hasError
        ? "group relative flex w-full items-center rounded-[22px] border-2 border-red-500 bg-red-50 px-4 py-3 transition duration-200"
        : [
            "group relative flex w-full items-center rounded-[22px] border bg-white/92 px-4 py-3 shadow-soft transition duration-200",
            "focus-within:border-primeColor/45 focus-within:shadow-[0_16px_38px_-28px_rgba(185,118,63,0.6)]",
            disabled ? "border-borderColor bg-lightGrey/70" : "border-borderColor",
            injectClass,
        ].join(" ");

    const baseInputClasses = "w-full bg-transparent text-sm font-medium text-tColor outline-none placeholder:text-tpColor/55";

    return (
        <motion.div className={wrapperClasses}>
            {label ? (
                <motion.div
                    className={`mb-2 flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-[0.18em] ${hasError ? "text-red-500" : "text-tpColor"} ${labelInjectClass || ''}`}
                >
                    <span>{label}</span>
                    {required && <span className="text-[#c44545]">*</span>}
                </motion.div>
            ) : null}

            {inputType === 'date' ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className={fieldContainerClasses}>
                        <DatePicker
                            value={fieldProps?.value ? dayjs(fieldProps.value) : null}
                            onChange={handleDateChange}
                            className={dateInjectClass}
                            minDate={minDate ? dayjs(minDate) : undefined}
                            maxDate={maxDate ? dayjs(maxDate) : undefined}
                            format="DD/MM/YYYY"
                            disabled={disabled || !edit}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: isRequired,
                                    variant: "standard",
                                    error: hasError,
                                    helperText: hasError ? fieldMeta.error : "",
                                    InputProps: { disableUnderline: true },
                                    sx: {
                                        "& .MuiInputBase-input": {
                                            fontSize: "0.95rem",
                                            fontWeight: 600,
                                            color: "#172033",
                                        },
                                        "& .MuiFormHelperText-root": {
                                            marginLeft: 0,
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                </LocalizationProvider>
            ) : inputType === 'textarea' ? (
                <div className={fieldContainerClasses}>
                    <TextField
                        type={inputType}
                        multiline
                        rows={4}
                        required={isRequired}
                        variant="standard"
                        fullWidth
                        disabled={!edit || disabled}
                        autoComplete={autoComplete}
                        placeholder={placeholder}
                        error={hasError}
                        helperText={hasError ? fieldMeta.error : ''}
                        InputProps={{
                            disableUnderline: true,
                            inputProps: {
                                maxLength: ml && length,
                                onKeyDown: handleEnterKey,
                            },
                            sx: {
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                color: "#172033",
                            },
                        }}
                        {...fieldProps}
                        onChange={handleInputChange}
                    />
                    {hasError && (
                        <IoIosAlert
                            className="ml-2 shrink-0 cursor-pointer text-xl text-[#c44545]"
                            onMouseEnter={() => setShowError(true)}
                            onMouseLeave={() => setShowError(false)}
                        />
                    )}
                </div>
            ) : (
                <div className={fieldContainerClasses}>
                    {type === 'phone' ? (
                        <PatternFormat
                            disabled={disabled || !edit}
                            className={`${baseInputClasses} py-1`}
                            getInputRef={input}
                            name={fieldProps.name}
                            value={fieldProps.value != null ? String(fieldProps.value) : ''}
                            displayType="input"
                            format="##########"
                            placeholder={placeholder || label}
                            onValueChange={(values) => {
                                runChange(values.value);
                                if (translate) translateFormHandling(values.value);
                            }}
                            onBlur={(e) => {
                                // Set touched so Formik runs validation
                                if (fieldHelper?.setTouched) fieldHelper.setTouched(true, true)
                                // Also call Formik's own onBlur if available
                                if (typeof fieldProps?.onBlur === 'function') fieldProps.onBlur(e)
                            }}
                            onKeyDown={(ev) => typeof onEnter === 'function' && handleEnterKey(ev)}
                        />
                    ) : type === 'file' ? (
                        <input
                            type="file"
                            ref={input}
                            className={`${baseInputClasses} cursor-pointer`}
                            {...fieldProps}
                            autoComplete={autoComplete}
                            disabled={!edit || disabled}
                            onChange={handleInputChange}
                        />
                    ) : type === 'textarea' ? null : (
                        <input
                            type={inputType}
                            ref={input}
                            className={`${baseInputClasses} py-1`}
                            maxLength={ml && length}
                            {...fieldProps}
                            autoComplete={autoComplete}
                            placeholder={placeholder || label}
                            disabled={!edit || disabled}
                            onChange={handleInputChange}
                            onKeyDown={(ev) => typeof onEnter === 'function' && handleEnterKey(ev)}
                        />
                    )}

                    {type === 'password' && (
                        <button type="button" className="ml-2 text-tpColor transition hover:text-primeColor" onClick={() => setShowPassword((prev) => !prev)}>
                            {showPassword ? <FaEye className="text-lg" /> : <FaEyeSlash className="text-lg" />}
                        </button>
                    )}

                    {hasError && (
                        <div className="relative ml-2 shrink-0">
                            <IoIosAlert
                                className="cursor-pointer text-xl text-[#c44545]"
                                onMouseEnter={() => setShowError(true)}
                                onMouseLeave={() => setShowError(false)}
                            />
                            {showError && (
                                <motion.div className="absolute right-0 top-8 z-20 w-56 rounded-2xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold leading-5 text-[#c44545] shadow-soft">
                                    {fieldMeta.error}
                                </motion.div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {hasError && (
                <p className="mt-1.5 px-2 text-xs font-semibold text-[#c44545]">{fieldMeta.error}</p>
            )}

            {instruction && <p className="px-1 pt-2 text-xs font-semibold text-tpColor">{instruction}</p>}

            {translate && translatedText && (
                <motion.div className="mt-3 rounded-2xl border border-primeColor/20 bg-primeColor/5 px-4 py-3">
                    <p
                        className="cursor-pointer text-sm font-bold text-primeColor"
                        onClick={() => {
                            if (typeof onChange === 'function') {
                                onChange({ translatedValue: translatedText, value: actualValue });
                            } else {
                                setFieldValue(translatedText);
                            }
                            setTranslatedText('');
                        }}
                    >
                        Use translation: {translatedText}
                    </p>
                </motion.div>
            )}
        </motion.div>
    );
};

export default FieldInput;
